<script setup lang="ts">
import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'

import { useProjectUserStore } from '@/stores/projectUser'
import { useAuthorizationStore } from '@/stores/authorization'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useProjectStore } from '@/stores/project'
import { useProjectUserPermissions } from '@/composables/useProjectUserPermissions'

import type { ProjectUserType } from '@/stores/type/projectUser.type'

import { AddUserDialog, DeleteDialog, UserManagementTable } from '@haskoning/vue-component-library'

const route = useRoute()
const router = useRouter()
const projectStore = useProjectStore()
const projectUserStore = useProjectUserStore()
const authorizationStore = useAuthorizationStore()
const authenticationStore = useDjangoAuthStore()
const {
  permissionsReady,
  canViewProjectUsers,
  canAddProjectUsers: canAdd,
  canEditProjectUsers: canEdit
} = useProjectUserPermissions()

const { currentItem: currentProject } = storeToRefs(projectStore)
const { user: currentUser, searchResults, searchLoading } = storeToRefs(authenticationStore)
const { items: roles } = storeToRefs(authorizationStore)
const {
  items: projectUserItems,
  addUserDialog,
  writeLoading,
  deleteDialog,
  deleteLoading,
  deleteItem
} = storeToRefs(projectUserStore)

watch(
  [permissionsReady, canViewProjectUsers],
  ([ready, canView]) => {
    if (ready && !canView && route.name === 'user-management') {
      void router.replace({
        name: 'calculation-index',
        params: { id: route.params.id }
      })
    }
  },
  { immediate: true }
)

const handleDelete = (item: ProjectUserType) => {
  projectUserStore.setDeleteItem(item)
  projectUserStore.openDeleteDialog()
}

const changeRole = async (payload: { item: any; newRole: number }) => {
  const { item, newRole } = payload
  if (item && newRole) {
    await projectUserStore.updateRole('project', item, newRole)
  }
}

const addUser = async (userId: number) => {
  const projectId = currentProject.value?.id
  if (!projectId) return
  await projectUserStore.addUser('project', projectId, userId)
}

const searchUsers = async (search: string) => {
  const projectId = currentProject.value?.id
  if (!projectId) return
  await authenticationStore.searchUsers('project', projectId, search)
}

const deleteProjectUser = async () => {
  if (deleteItem.value) {
    await projectUserStore.delete(deleteItem.value)
  }
}
</script>

<template>
  <div v-if="canViewProjectUsers" class="px-8 py-6">
    <header class="user-management-heading">
      <div class="user-management-title-row">
        <h1 class="user-management-title">User Management</h1>
        <v-tooltip v-if="canAdd" text="Invite people to project">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              class="ml-3 text-none"
              prepend-icon="mdi-plus"
              color="primary"
              flat
              @click="addUserDialog = true"
            >
              Add
            </v-btn>
          </template>
        </v-tooltip>
      </div>
      <div class="user-management-subtitle">
        <template v-if="canAdd">
          Please configure the permissions for users within this project, add a new user via the
          <v-btn
            class="mx-1 cursor-default text-none"
            prepend-icon="mdi-plus"
            size="small"
            color="primary"
            flat
            readonly
          >
            Add
          </v-btn>
          button.
        </template>
        <template v-else-if="canEdit">
          Please configure the permissions for users within this project using the table below.
        </template>
        <template v-else-if="currentProject?.is_demo">
          View the permissions for users within this project. Access to the demo project is managed
          automatically.
        </template>
        <template v-else>
          View the permissions for users within this project.
        </template>
      </div>
    </header>

    <v-card border="opacity-35 sm" class="mx-auto mt-6" variant="text">
      <v-card-text>
        <UserManagementTable
          v-if="currentUser"
          :current-user="currentUser"
          :roles="roles"
          :auth-model-users="projectUserItems"
          :can-edit="canEdit"
          auth-role="ProjectUser"
          @handle-delete="handleDelete"
          @set-selected-item="deleteItem = $event"
          @change-role="changeRole"
        />
      </v-card-text>
    </v-card>

    <AddUserDialog
      v-model="addUserDialog"
      :search-results="searchResults"
      :add-loading="writeLoading"
      :search-loading="searchLoading"
      @add-user="addUser"
      @search-users="searchUsers"
      @reset-state="authenticationStore.resetSearchState()"
    />

    <DeleteDialog
      v-if="deleteItem"
      v-model="deleteDialog"
      :name="deleteItem.email"
      :item="deleteItem"
      :loading="deleteLoading"
      @set-delete-item="deleteItem = $event"
      @delete="deleteProjectUser"
    />
  </div>
</template>

<style scoped>
.user-management-heading {
  margin: 0;
}

.user-management-title-row {
  align-items: center;
  display: flex;
}

.user-management-title {
  font-size: 24px;
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
}

.user-management-subtitle {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 16px;
  line-height: 1.4;
  margin: 4px 0 0;
}
</style>
